import { useEffect, useState } from 'react';
import { Download, RefreshCw, ShieldCheck } from 'lucide-react';
import { text, type Locale } from '../shared/content';
import { api, ApiError, post } from './api';
import { errorText } from './i18n';

type PrivacyRequest = {
  id: string;
  kind: 'export' | 'delete';
  status: 'pending' | 'completed' | 'expired' | 'cancelled';
  createdAt: string;
  completedAt: string | null;
  receipt: Record<string, unknown>;
  receiptToken?: string;
};
type PrivateReceipt = { format: 'tavmix-private-privacy-receipt-v1'; id: string; token: string };
const copy = {
  title: text('Your data', '你的数据', '你的資料', 'Tus datos'),
  intro: text(
    'Export your data or request account closure. These channels remain available without a character and during gameplay restrictions.',
    '导出自己的数据，或申请关闭账号。未创建角色或游戏权限受限时，这些渠道仍可使用。',
    '匯出自己的資料，或申請關閉帳號。未建立角色或遊戲權限受限時，這些管道仍可使用。',
    'Exporta tus datos o solicita el cierre de cuenta. Puedes usar este canal sin personaje o con restricciones del juego.',
  ),
  notice: text(
    'Read the data and privacy notice',
    '查看数据与隐私说明',
    '查看資料與隱私說明',
    'Leer el aviso de datos y privacidad',
  ),
  lookup: text('Look up a private receipt', '查询私密回执', '查詢私密回執', 'Consultar un recibo privado'),
  type: text('Request type', '请求类型', '請求類型', 'Tipo de solicitud'),
  export: text('Export my data', '导出我的数据', '匯出我的資料', 'Exportar mis datos'),
  delete: text('Request account closure', '申请关闭账号', '申請關閉帳號', 'Solicitar cierre de cuenta'),
  password: text('Current game password', '当前游戏密码', '目前遊戲密碼', 'Contraseña actual del juego'),
  passwordNote: text(
    'Use your game password, not your email password. It is cleared from this form as soon as you submit and is not saved in downloaded files.',
    '请填写游戏密码，不是邮箱密码。提交后表单会立即清空密码，下载文件也不会保存它。',
    '請填寫遊戲密碼，不是信箱密碼。提交後表單會立即清空密碼，下載檔案也不會儲存它。',
    'Usa la contraseña del juego, no la de tu correo. Se borra del formulario al enviar y no se guarda en los archivos descargados.',
  ),
  deletionNote: text(
    'This submits a request for an operator to process; it does not delete or close your account now. Necessary financial, election and security records may be retained.',
    '这只会提交待运营人员处理的申请，不会立即删除或关闭账号。必要的财务、选举和安全记录可能保留。',
    '這只會提交待營運人員處理的申請，不會立即刪除或關閉帳號。必要的財務、選舉和安全紀錄可能保留。',
    'Solo envía una solicitud pendiente de un operador; no borra ni cierra la cuenta ahora. Pueden conservarse registros financieros, electorales y de seguridad necesarios.',
  ),
  confirm: text(
    'I want to submit this account-closure request.',
    '我确认提交这项账号关闭申请。',
    '我確認提交這項帳號關閉申請。',
    'Confirmo que quiero enviar esta solicitud de cierre.',
  ),
  submit: text('Submit request', '提交请求', '提交請求', 'Enviar solicitud'),
  saved: text(
    'Request saved. Account closure still requires operator processing.',
    '申请已保存，账号关闭仍须运营人员处理。',
    '申請已儲存，帳號關閉仍須營運人員處理。',
    'Solicitud guardada. El cierre sigue pendiente de un operador.',
  ),
  exportSaved: text(
    'Export request saved. Re-enter your password below to download it.',
    '导出请求已保存。请重新输入密码，再点击下方下载。',
    '匯出請求已儲存。請重新輸入密碼，再點擊下方下載。',
    'Solicitud guardada. Vuelve a introducir tu contraseña y descarga el archivo abajo.',
  ),
  history: text('Your data requests', '你的数据请求记录', '你的資料請求紀錄', 'Tus solicitudes de datos'),
  empty: text('No data requests yet.', '尚无数据请求。', '尚無資料請求。', 'Aún no hay solicitudes.'),
  loading: text('Loading your requests…', '正在读取请求…', '正在讀取請求…', 'Cargando solicitudes…'),
  refresh: text('Refresh requests', '刷新请求状态', '重新整理請求狀態', 'Actualizar solicitudes'),
  pendingDelete: text(
    'Pending operator processing · account not closed by this request yet',
    '待运营人员处理 · 该申请尚未完成账号关闭',
    '待營運人員處理 · 此申請尚未完成帳號關閉',
    'Pendiente del operador · esta solicitud aún no ha cerrado la cuenta',
  ),
  completedDelete: text(
    'Account-closure processing completed · necessary records remain',
    '账号关闭处理已完成 · 必要记录仍保留',
    '帳號關閉處理已完成 · 必要紀錄仍保留',
    'Cierre procesado · se conservan registros necesarios',
  ),
  pendingExport: text(
    'Export request registered',
    '导出请求已登记',
    '匯出請求已登記',
    'Solicitud de exportación registrada',
  ),
  completedExport: text('Export generated', '导出已生成', '匯出已產生', 'Exportación generada'),
  expired: text('Export request expired · submit a new request', '导出请求已过期 · 请重新提交', '匯出請求已過期 · 請重新提交', 'Solicitud caducada · envía una nueva'),
  cancelled: text('Request cancelled when the account was closed', '账号关闭时，此请求已取消', '帳號關閉時，此請求已取消', 'Solicitud cancelada al cerrar la cuenta'),
  requestId: text('Request ID', '请求编号', '請求編號', 'ID de solicitud'),
  download: text(
    'Download export · verify password again',
    '下载导出文件 · 再次验证密码',
    '下載匯出檔案 · 再次驗證密碼',
    'Descargar exportación · verificar contraseña de nuevo',
  ),
  downloaded: text(
    'Export prepared for download. Keep the file private.',
    '导出文件已准备下载，请妥善保管。',
    '匯出檔案已準備下載，請妥善保管。',
    'Exportación preparada para descargar. Guarda el archivo en privado.',
  ),
  exportNote: text(
    'Exports can be downloaded for 24 hours after requesting. Downloaded files may contain your email and original content.',
    '导出请求提交后 24 小时内可下载。文件可能包含你的邮箱及内容原文。',
    '匯出請求提交後 24 小時內可下載。檔案可能包含你的信箱及內容原文。',
    'La descarga está disponible durante 24 horas desde la solicitud. El archivo puede contener tu correo y textos originales.',
  ),
  saveCredential: text(
    'Save private lookup credential',
    '保存私密查询凭证',
    '儲存私密查詢憑證',
    'Guardar credencial privada de consulta',
  ),
  credentialNote: text(
    'Save this JSON file before account closure. It contains a private token, not your password, and allows lookup after signing out. Keep it private; do not post or share it. If you missed saving it, submit the same request type again while you can still sign in.',
    '请在账号关闭前保存此 JSON 文件。它含私密查询令牌，不含密码，退出登录后仍可使用。请勿公开或分享。若未保存，可在仍能登录时再次提交同类请求获取凭证。',
    '請在帳號關閉前儲存此 JSON 檔案。它含私密查詢權杖，不含密碼，登出後仍可使用。請勿公開或分享。若未儲存，可在仍能登入時再次提交同類請求取得憑證。',
    'Guarda este JSON antes del cierre. Contiene un token privado, no tu contraseña, y permite consultar tras salir. No lo publiques ni compartas. Si no lo guardaste, vuelve a enviar el mismo tipo de solicitud mientras puedas iniciar sesión.',
  ),
  receiptIntro: text(
    'Use the private credential saved with your closure request. You do not need to sign in or enter your game password.',
    '使用提交关闭申请时保存的私密凭证，无须登录或填写游戏密码。',
    '使用提交關閉申請時儲存的私密憑證，無須登入或填寫遊戲密碼。',
    'Usa la credencial privada guardada con tu solicitud de cierre. No necesitas iniciar sesión ni introducir tu contraseña.',
  ),
  import: text(
    'Open private credential JSON',
    '读取私密凭证 JSON',
    '讀取私密憑證 JSON',
    'Abrir JSON de credencial privada',
  ),
  token: text('Private lookup token', '私密查询令牌', '私密查詢權杖', 'Token privado de consulta'),
  query: text('Check request status', '查询请求状态', '查詢請求狀態', 'Consultar estado'),
  saveReceipt: text(
    'Save this status receipt',
    '保存这份状态回执',
    '儲存這份狀態回執',
    'Guardar este recibo de estado',
  ),
  badFile: text(
    'Choose a valid private receipt file (up to 16 KB).',
    '请选择有效的私密凭证文件（不超过 16 KB）。',
    '請選擇有效的私密憑證檔案（不超過 16 KB）。',
    'Elige un archivo de credencial válido (hasta 16 KB).',
  ),
  busy: text('Working…', '正在处理…', '正在處理…', 'Procesando…'),
};
const errors = {
  PRIVACY_REAUTH: text(
    'Enter your current game password.',
    '请填写正确的当前游戏密码。',
    '請填寫正確的目前遊戲密碼。',
    'Introduce tu contraseña actual del juego.',
  ),
  PRIVACY_REAUTH_LIMIT: text(
    'Too many password checks. Try again in fifteen minutes.',
    '密码验证次数过多，请十五分钟后重试。',
    '密碼驗證次數過多，請十五分鐘後重試。',
    'Demasiadas verificaciones. Reinténtalo en quince minutos.',
  ),
  PRIVACY_REQUEST_LIMIT: text(
    'The request limit has been reached. Your existing requests remain available.',
    '请求次数已达上限，已有请求仍可查看。',
    '請求次數已達上限，既有請求仍可查看。',
    'Se alcanzó el límite. Las solicitudes existentes siguen disponibles.',
  ),
  PRIVACY_EXPORT_EXPIRED: text(
    'This export request expired. Submit a new one.',
    '这项导出请求已过期，请重新提交。',
    '此匯出請求已過期，請重新提交。',
    'Esta solicitud de exportación caducó. Envía una nueva.',
  ),
  PRIVACY_REQUEST: text(
    'This request is unavailable.',
    '此请求不可用。',
    '此請求無法使用。',
    'Esta solicitud no está disponible.',
  ),
  PRIVACY_RECEIPT: text(
    'No matching receipt. Check the ID and private token.',
    '未找到匹配回执，请检查编号和私密令牌。',
    '找不到相符回執，請檢查編號和私密權杖。',
    'No se encontró el recibo. Revisa el ID y el token privado.',
  ),
  ACCOUNT_DELETED: text(
    'This account is closed. Use your saved private receipt to check its status.',
    '账号已关闭，请使用已保存的私密凭证查询回执。',
    '帳號已關閉，請使用已儲存的私密憑證查詢回執。',
    'La cuenta está cerrada. Usa la credencial guardada para consultar el recibo.',
  ),
};
const messageFor = (locale: Locale, error: unknown) =>
  error instanceof ApiError && error.code in errors
    ? errors[error.code as keyof typeof errors][locale]
    : errorText(locale, error);
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parsePrivateReceiptFile(source: string): PrivateReceipt {
  if (new Blob([source]).size > 16384) throw new Error('Invalid private receipt file.');
  const value = JSON.parse(source);
  if (
    !value ||
    value.format !== 'tavmix-private-privacy-receipt-v1' ||
    typeof value.id !== 'string' ||
    !uuid.test(value.id) ||
    typeof value.token !== 'string' ||
    !/^[A-Za-z0-9_-]{32,128}$/.test(value.token) ||
    Object.keys(value).some((key) => !['format', 'id', 'token'].includes(key))
  )
    throw new Error('Invalid private receipt file.');
  return { format: value.format, id: value.id, token: value.token };
}
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function saveJson(value: unknown, filename: string) {
  downloadBlob(new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' }), filename);
}

export default function PrivacyPanel({
  locale,
  mode = 'account',
}: {
  locale: Locale;
  mode?: 'account' | 'receipt';
}) {
  const t = (key: keyof typeof copy) => copy[key][locale];
  const [requests, setRequests] = useState<PrivacyRequest[]>();
  const [kind, setKind] = useState<'export' | 'delete'>('export');
  const [password, setPassword] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(false);
  const [credential, setCredential] = useState<PrivateReceipt>();
  const [receiptId, setReceiptId] = useState('');
  const [receiptToken, setReceiptToken] = useState('');
  const [receipt, setReceipt] = useState<PrivacyRequest>();
  const fail = (e: unknown) => {
    setError(true);
    setMessage(messageFor(locale, e));
  };
  const refresh = async () => {
    try {
      setRequests((await api<{ requests: PrivacyRequest[] }>('/privacy/requests')).requests);
    } catch (e) {
      fail(e);
    }
  };
  useEffect(() => {
    if (mode !== 'account') return;
    const controller = new AbortController();
    api<{ requests: PrivacyRequest[] }>('/privacy/requests', { signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setRequests(data.requests);
      })
      .catch((e) => {
        if (!controller.signal.aborted) fail(e);
      });
    return () => controller.abort();
  }, [mode]);
  const status = (record: PrivacyRequest) =>
    record.status === 'expired' || record.status === 'cancelled'
      ? t(record.status)
      : record.kind === 'delete'
      ? t(record.status === 'completed' ? 'completedDelete' : 'pendingDelete')
      : t(record.status === 'completed' ? 'completedExport' : 'pendingExport');
  const date = (value: string) =>
    Number.isFinite(Date.parse(value))
      ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
      : '—';
  const downloadExport = async (id: string) => {
    const body = JSON.stringify({ password });
    setPassword('');
    setBusy(true);
    setMessage('');
    setError(false);
    try {
      const response = await fetch(`/api/privacy/requests/${id}/export`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal: AbortSignal.timeout(30000),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new ApiError(data.code || 'REQUEST', data.message || 'Export unavailable.', response.status);
      }
      downloadBlob(await response.blob(), `tavmix-data-${id}.json`);
      setMessage(t('downloaded'));
      await refresh();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section style={{ textAlign: 'left' }} aria-label={mode === 'receipt' ? t('lookup') : t('title')}>
      <ShieldCheck size={26} />
      <h2>{mode === 'receipt' ? t('lookup') : t('title')}</h2>
      <p>{mode === 'receipt' ? t('receiptIntro') : t('intro')}</p>
      <p>
        <a href={`/privacy?lang=${locale}`} target="_blank" rel="noopener noreferrer">
          {t('notice')}
        </a>
      </p>
      {mode === 'account' ? (
        <>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (busy || !password || (kind === 'delete' && !confirmed)) return;
              const input = { kind, requestId: crypto.randomUUID(), password };
              setPassword('');
              setBusy(true);
              setMessage('');
              setError(false);
              try {
                const record = await post<PrivacyRequest>('/privacy/requests', input);
                if (record.receiptToken)
                  setCredential({
                    format: 'tavmix-private-privacy-receipt-v1',
                    id: record.id,
                    token: record.receiptToken,
                  });
                setMessage(t(record.kind === 'delete' ? 'saved' : 'exportSaved'));
                setConfirmed(false);
                await refresh();
              } catch (e) {
                fail(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            <label>
              {t('type')}
              <select
                value={kind}
                disabled={busy}
                onChange={(e) => {
                  setKind(e.target.value as 'export' | 'delete');
                  setConfirmed(false);
                }}
              >
                <option value="export">{t('export')}</option>
                <option value="delete">{t('delete')}</option>
              </select>
            </label>
            <label>
              {t('password')}
              <input
                type="password"
                autoComplete="current-password"
                required
                maxLength={128}
                value={password}
                disabled={busy}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <p className="muted tiny">{t('passwordNote')}</p>
            {kind === 'delete' && (
              <>
                <p>{t('deletionNote')}</p>
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    required
                    checked={confirmed}
                    disabled={busy}
                    style={{ width: 'auto', height: 'auto', marginTop: 3 }}
                    onChange={(e) => setConfirmed(e.target.checked)}
                  />
                  <span>{t('confirm')}</span>
                </label>
              </>
            )}
            <button
              className="button primary full"
              disabled={busy || !password || (kind === 'delete' && !confirmed)}
            >
              {busy ? t('busy') : t('submit')}
            </button>
          </form>
          {credential && (
            <div className="account-restriction">
              <p>{t('credentialNote')}</p>
              <small style={{ overflowWrap: 'anywhere' }}>
                {t('requestId')}: {credential.id}
              </small>
              <button
                className="button primary full"
                onClick={() => saveJson(credential, `tavmix-private-receipt-${credential.id}.json`)}
              >
                <Download size={15} />
                {t('saveCredential')}
              </button>
            </div>
          )}
          <h3>{t('history')}</h3>
          <p className="muted tiny">{t('exportNote')}</p>
          {!requests ? (
            <p role="status">{t('loading')}</p>
          ) : !requests.length ? (
            <p>{t('empty')}</p>
          ) : (
            requests.map((record) => (
              <article className="appeal-record" key={record.id}>
                <strong>{record.kind === 'delete' ? t('delete') : t('export')}</strong>
                <p>{status(record)}</p>
                <small style={{ display: 'block', overflowWrap: 'anywhere' }}>
                  {t('requestId')}: {record.id}
                </small>
                <small>{date(record.createdAt)}</small>
                {record.kind === 'export' && (record.status === 'pending' || record.status === 'completed') && (
                  <button
                    className="button soft full"
                    disabled={busy || !password}
                    onClick={() => downloadExport(record.id)}
                  >
                    <Download size={14} />
                    {t('download')}
                  </button>
                )}
              </article>
            ))
          )}
          <button className="button soft full" disabled={busy} onClick={refresh}>
            <RefreshCw size={14} />
            {t('refresh')}
          </button>
          <p>
            <a href="/?privacy=receipt">{t('lookup')}</a>
          </p>
        </>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (busy || !receiptId || !receiptToken) return;
            const input = { id: receiptId.trim(), token: receiptToken.trim() };
            setReceiptToken('');
            setReceipt(undefined);
            setBusy(true);
            setMessage('');
            setError(false);
            try {
              setReceipt(await post<PrivacyRequest>('/privacy/receipt', input));
            } catch (e) {
              fail(e);
            } finally {
              setBusy(false);
            }
          }}
        >
          <label>
            {t('import')}
            <input
              type="file"
              accept="application/json,.json"
              disabled={busy}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                try {
                  if (file.size > 16384) throw new Error('Invalid private receipt file.');
                  const saved = parsePrivateReceiptFile(await file.text());
                  setReceiptId(saved.id);
                  setReceiptToken(saved.token);
                  setMessage('');
                  setError(false);
                  setReceipt(undefined);
                } catch {
                  setError(true);
                  setMessage(t('badFile'));
                }
              }}
            />
          </label>
          <label>
            {t('requestId')}
            <input
              required
              autoComplete="off"
              maxLength={36}
              value={receiptId}
              onChange={(e) => setReceiptId(e.target.value)}
            />
          </label>
          <label>
            {t('token')}
            <input
              required
              type="password"
              autoComplete="off"
              minLength={32}
              maxLength={128}
              spellCheck={false}
              value={receiptToken}
              onChange={(e) => setReceiptToken(e.target.value)}
            />
          </label>
          <button
            className="button primary full"
            disabled={busy || !uuid.test(receiptId.trim()) || receiptToken.trim().length < 32}
          >
            {busy ? t('busy') : t('query')}
          </button>
        </form>
      )}
      {receipt && (
        <article className="appeal-record">
          <strong>{status(receipt)}</strong>
          <p style={{ overflowWrap: 'anywhere' }}>
            {t('requestId')}: {receipt.id}
          </p>
          <small>{date(receipt.completedAt || receipt.createdAt)}</small>
          <button
            className="button soft full"
            onClick={() => saveJson(receipt, `tavmix-status-receipt-${receipt.id}.json`)}
          >
            <Download size={14} />
            {t('saveReceipt')}
          </button>
        </article>
      )}
      {message && (
        <p className={error ? 'form-error' : 'green'} role={error ? 'alert' : 'status'}>
          {message}
        </p>
      )}
    </section>
  );
}
